"use strict";
/**
 * Unified Firestore Service
 * Provides a consistent API for Firestore operations that works seamlessly across web and native platforms
 * - Automatically routes to Native Firestore Adapter on iOS/Android
 * - Uses web Firebase SDK on browsers
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.unifiedAddDoc = exports.unifiedDeleteDoc = exports.unifiedUpdateDoc = exports.unifiedSetDoc = exports.unifiedGetDoc = exports.unifiedCollection = exports.UnifiedFirestoreQuery = void 0;
const core_1 = require("@capacitor/core");
const firestore_1 = require("firebase/firestore");
const firebaseManager_1 = require("./firebaseManager");
const nativeFirestoreAdapter_1 = require("./nativeFirestoreAdapter");
/**
 * Unified query builder that works on both web and native platforms
 */
class UnifiedFirestoreQuery {
    constructor(collectionPath) {
        this.whereConstraints = [];
        this.orderByConstraints = [];
        this.collectionPath = collectionPath;
    }
    where(fieldPath, opStr, value) {
        this.whereConstraints.push({ fieldPath, opStr, value });
        return this;
    }
    orderBy(fieldPath, direction = 'asc') {
        this.orderByConstraints.push({ fieldPath, direction });
        return this;
    }
    limit(count) {
        this.limitCount = count;
        return this;
    }
    startAfter(docId) {
        this.startAfterDocId = docId;
        return this;
    }
    /**
     * Execute the query and return documents
     */
    getDocs() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const isNative = core_1.Capacitor.isNativePlatform();
            console.log('🔍 UnifiedFirestore getDocs:', {
                collectionPath: this.collectionPath,
                isNative,
                hasCapacitor: typeof core_1.Capacitor !== 'undefined',
                platform: (_a = core_1.Capacitor.getPlatform) === null || _a === void 0 ? void 0 : _a.call(core_1.Capacitor),
                whereConstraints: this.whereConstraints.length,
                orderByConstraints: this.orderByConstraints.length,
                limitCount: this.limitCount
            });
            if (isNative) {
                // Use Native Firestore Adapter
                console.log(`📱 UnifiedFirestore: Using NATIVE adapter for collection: ${this.collectionPath}`);
                try {
                    const result = yield nativeFirestoreAdapter_1.nativeFirestoreAdapter.getCollection(this.collectionPath, this.whereConstraints, this.orderByConstraints, this.limitCount, this.startAfterDocId);
                    console.log(`✅ UnifiedFirestore: Native query successful, got ${result.documents.length} documents`);
                    // Convert to web SDK-like format for compatibility
                    return result.documents.map((doc) => ({
                        id: doc.id,
                        data: () => doc.data
                    }));
                }
                catch (error) {
                    // On native, do NOT fall back to web SDK - it will hang indefinitely
                    // The web SDK Firestore competes with the native plugin for the connection
                    console.error('❌ UnifiedFirestore: Native query failed:', error);
                    throw error;
                }
            }
            else {
                // Use web Firebase SDK
                console.log(`🌐 UnifiedFirestore: Using WEB SDK for collection: ${this.collectionPath}`);
                return this.getDocsWeb();
            }
        });
    }
    /**
     * Internal method to get docs using web SDK
     */
    getDocsWeb() {
        return __awaiter(this, void 0, void 0, function* () {
            // getDb() might be async on web platform, handle both cases
            let db = (0, firebaseManager_1.getDb)();
            if (db && typeof db.then === 'function') {
                // It's a Promise, await it
                db = yield db;
            }
            const collectionRef = (0, firestore_1.collection)(db, this.collectionPath);
            const constraints = [];
            // Add where clauses
            for (const constraint of this.whereConstraints) {
                constraints.push((0, firestore_1.where)(constraint.fieldPath, constraint.opStr, constraint.value));
            }
            // Add orderBy constraints
            for (const orderBy of this.orderByConstraints) {
                constraints.push((0, firestore_1.orderBy)(orderBy.fieldPath, orderBy.direction));
            }
            // Add startAfter cursor if specified
            if (this.startAfterDocId) {
                const cursorRef = (0, firestore_1.doc)(db, this.collectionPath, this.startAfterDocId);
                constraints.push((0, firestore_1.startAfter)(cursorRef));
            }
            // Add limit if specified
            if (this.limitCount) {
                constraints.push((0, firestore_1.limit)(this.limitCount));
            }
            const q = constraints.length > 0 ? (0, firestore_1.query)(collectionRef, ...constraints) : collectionRef;
            const snapshot = yield (0, firestore_1.getDocs)(q);
            console.log(`✅ UnifiedFirestore: Web query successful, got ${snapshot.docs.length} documents`);
            return snapshot.docs;
        });
    }
}
exports.UnifiedFirestoreQuery = UnifiedFirestoreQuery;
/**
 * Create a unified Firestore query
 * Works seamlessly on both web and native platforms
 */
function unifiedCollection(collectionPath) {
    return new UnifiedFirestoreQuery(collectionPath);
}
exports.unifiedCollection = unifiedCollection;
/**
 * Helper to get a single document by ID (works on both platforms)
 */
function unifiedGetDoc(collectionPath, docId) {
    return __awaiter(this, void 0, void 0, function* () {
        const isNative = core_1.Capacitor.isNativePlatform();
        if (isNative) {
            console.log(`📱 UnifiedFirestore: Getting document (NATIVE): ${collectionPath}/${docId}`);
            try {
                const result = yield nativeFirestoreAdapter_1.nativeFirestoreAdapter.getDocument(`${collectionPath}/${docId}`);
                return {
                    exists: () => result !== null,
                    id: docId,
                    data: () => (result === null || result === void 0 ? void 0 : result.data) || result
                };
            }
            catch (error) {
                // On native, do NOT fall back to web SDK - it will hang indefinitely
                console.error('❌ UnifiedFirestore: Native getDoc failed:', error);
                throw error;
            }
        }
        else {
            return unifiedGetDocWeb(collectionPath, docId);
        }
    });
}
exports.unifiedGetDoc = unifiedGetDoc;
/**
 * Internal helper to get document using web SDK
 */
function unifiedGetDocWeb(collectionPath, docId) {
    return __awaiter(this, void 0, void 0, function* () {
        console.log(`🌐 UnifiedFirestore: Getting document (WEB): ${collectionPath}/${docId}`);
        const { doc, getDoc } = yield Promise.resolve().then(() => __importStar(require('firebase/firestore')));
        const db = (0, firebaseManager_1.getDb)();
        const docRef = doc(db, collectionPath, docId);
        const snapshot = yield getDoc(docRef);
        return snapshot;
    });
}
/**
 * Set a document (create or overwrite) - works on both platforms
 */
function unifiedSetDoc(collectionPath, docId, data, merge = false) {
    return __awaiter(this, void 0, void 0, function* () {
        const isNative = core_1.Capacitor.isNativePlatform();
        if (isNative) {
            console.log(`📱 UnifiedFirestore: Setting document (NATIVE): ${collectionPath}/${docId}`);
            try {
                yield nativeFirestoreAdapter_1.nativeFirestoreAdapter.setDocument(`${collectionPath}/${docId}`, data, merge);
            }
            catch (error) {
                // On native, do NOT fall back to web SDK - it will hang indefinitely
                console.error('❌ UnifiedFirestore: Native setDoc failed:', error);
                throw error;
            }
        }
        else {
            return unifiedSetDocWeb(collectionPath, docId, data, merge);
        }
    });
}
exports.unifiedSetDoc = unifiedSetDoc;
/**
 * Internal helper to set document using web SDK
 */
function unifiedSetDocWeb(collectionPath, docId, data, merge = false) {
    return __awaiter(this, void 0, void 0, function* () {
        console.log(`🌐 UnifiedFirestore: Setting document (WEB): ${collectionPath}/${docId}`);
        const { doc, setDoc } = yield Promise.resolve().then(() => __importStar(require('firebase/firestore')));
        const db = (0, firebaseManager_1.getDb)();
        const docRef = doc(db, collectionPath, docId);
        if (merge) {
            yield setDoc(docRef, data, { merge: true });
        }
        else {
            yield setDoc(docRef, data);
        }
    });
}
/**
 * Update a document (partial update) - works on both platforms
 */
function unifiedUpdateDoc(collectionPath, docId, data) {
    return __awaiter(this, void 0, void 0, function* () {
        const isNative = core_1.Capacitor.isNativePlatform();
        if (isNative) {
            console.log(`📱 UnifiedFirestore: Updating document (NATIVE): ${collectionPath}/${docId}`);
            try {
                yield nativeFirestoreAdapter_1.nativeFirestoreAdapter.updateDocument(`${collectionPath}/${docId}`, data);
            }
            catch (error) {
                // On native, do NOT fall back to web SDK - it will hang indefinitely
                console.error('❌ UnifiedFirestore: Native updateDoc failed:', error);
                throw error;
            }
        }
        else {
            return unifiedUpdateDocWeb(collectionPath, docId, data);
        }
    });
}
exports.unifiedUpdateDoc = unifiedUpdateDoc;
/**
 * Internal helper to update document using web SDK
 */
function unifiedUpdateDocWeb(collectionPath, docId, data) {
    return __awaiter(this, void 0, void 0, function* () {
        console.log(`🌐 UnifiedFirestore: Updating document (WEB): ${collectionPath}/${docId}`);
        const { doc, updateDoc } = yield Promise.resolve().then(() => __importStar(require('firebase/firestore')));
        const db = (0, firebaseManager_1.getDb)();
        const docRef = doc(db, collectionPath, docId);
        yield updateDoc(docRef, data);
    });
}
/**
 * Delete a document - works on both platforms
 */
function unifiedDeleteDoc(collectionPath, docId) {
    return __awaiter(this, void 0, void 0, function* () {
        const isNative = core_1.Capacitor.isNativePlatform();
        if (isNative) {
            console.log(`📱 UnifiedFirestore: Deleting document (NATIVE): ${collectionPath}/${docId}`);
            try {
                yield nativeFirestoreAdapter_1.nativeFirestoreAdapter.deleteDocument(`${collectionPath}/${docId}`);
            }
            catch (error) {
                // On native, do NOT fall back to web SDK - it will hang indefinitely
                console.error('❌ UnifiedFirestore: Native deleteDoc failed:', error);
                throw error;
            }
        }
        else {
            return unifiedDeleteDocWeb(collectionPath, docId);
        }
    });
}
exports.unifiedDeleteDoc = unifiedDeleteDoc;
/**
 * Internal helper to delete document using web SDK
 */
function unifiedDeleteDocWeb(collectionPath, docId) {
    return __awaiter(this, void 0, void 0, function* () {
        console.log(`🌐 UnifiedFirestore: Deleting document (WEB): ${collectionPath}/${docId}`);
        const { doc, deleteDoc } = yield Promise.resolve().then(() => __importStar(require('firebase/firestore')));
        const db = (0, firebaseManager_1.getDb)();
        const docRef = doc(db, collectionPath, docId);
        yield deleteDoc(docRef);
    });
}
/**
 * Add a new document with auto-generated ID - works on both platforms
 */
function unifiedAddDoc(collectionPath, data) {
    return __awaiter(this, void 0, void 0, function* () {
        const isNative = core_1.Capacitor.isNativePlatform();
        if (isNative) {
            console.log(`📱 UnifiedFirestore: Adding document (NATIVE) to: ${collectionPath}`);
            try {
                const docId = yield nativeFirestoreAdapter_1.nativeFirestoreAdapter.addDocument(collectionPath, data);
                return docId;
            }
            catch (error) {
                // On native, do NOT fall back to web SDK - it will hang indefinitely
                console.error('❌ UnifiedFirestore: Native addDoc failed:', error);
                throw error;
            }
        }
        else {
            return unifiedAddDocWeb(collectionPath, data);
        }
    });
}
exports.unifiedAddDoc = unifiedAddDoc;
/**
 * Internal helper to add document using web SDK
 */
function unifiedAddDocWeb(collectionPath, data) {
    return __awaiter(this, void 0, void 0, function* () {
        console.log(`🌐 UnifiedFirestore: Adding document (WEB) to: ${collectionPath}`);
        const { collection, addDoc } = yield Promise.resolve().then(() => __importStar(require('firebase/firestore')));
        const db = (0, firebaseManager_1.getDb)();
        const collectionRef = collection(db, collectionPath);
        const docRef = yield addDoc(collectionRef, data);
        return docRef.id;
    });
}
